<template>
    <div>
        <div v-if="!saved && field === 'email'" class="my-6">
            <label for="email" class="block text-sm font-medium text-gray-700">
                Email
            </label>
            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    @keydown.enter="onSubmit"
                    v-model="inputs.email"
                    id="email"
                    name="email"
                    type="email"
                    autocomplete="email"
                    :class="[
                        errors.email
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="errors.email ? 'true' : 'false'"
                    :aria-describedby="errors.email ? 'email-error' : ''"
                />
                <div
                    v-if="errors.email"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="errors.email"
                class="mt-2 text-sm text-red-600"
                id="email-error"
            >
                {{ errors.email }}
            </p>
        </div>

        <div v-if="!saved && field === 'phone'" class="my-6">
            <label for="phone" class="block text-sm font-medium text-gray-700">
                Phone
            </label>

            <div class="mt-1 relative rounded-md shadow-sm">
                <input
                    @keydown.enter="onSubmit"
                    v-model="inputs.phone"
                    name="phone"
                    id="phone"
                    type="tel"
                    autocomplete="phone"
                    :class="[
                        errors.phone
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                    ]"
                    :aria-invalid="errors.phone ? 'true' : 'false'"
                    :aria-describedby="errors.phone ? 'phone-error' : ''"
                />
                <div
                    v-if="errors.phone"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                >
                    <ExclamationCircleIcon
                        class="h-5 w-5 text-red-500"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <p
                v-if="errors.phone"
                class="mt-2 text-sm text-red-600"
                id="phone-error"
            >
                {{ errors.phone }}
            </p>
        </div>

        <div class="mt-6 mb-3">
            <div
                v-if="saved"
                class="flex items-center justify-center text-nutreance-green"
            >
                <CheckCircleIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                Your {{ field }} has been saved!
            </div>

            <button
                v-else
                name="save"
                @click="onSubmit"
                class="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-md shadow-sm font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
            >
                <LoadingIcon v-if="loading" class="h-5 w-5 mr-2" />
                <CheckIcon v-else class="h-5 w-5 mr-2" aria-hidden="true" />
                Save My {{ startCase(field) }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, reactive, ref } from "vue";
import {
    CheckIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
} from "@heroicons/vue/solid";
import LoadingIcon from "../LoadingIcon.vue";
import { startCase } from "lodash";
import { completeShippingAddress } from "../../api/checkout";
import type { AppEmitter } from "../../emitter";

defineProps<{
    field?: string;
}>();

const emitter = inject<AppEmitter>("mitt")!;
const inputs = reactive({
    email: "",
    phone: "",
});

const errors = reactive<Record<string, string>>({
    email: "",
    phone: "",
});

const loading = ref(false);
const saved = ref(false);

function clearErrors() {
    for (const field in errors) {
        delete errors[field];
    }
}

function onSubmit() {
    if (loading.value || saved.value) {
        return;
    }

    loading.value = true;

    completeShippingAddress(inputs)
        .then(() => {
            clearErrors();
            saved.value = true;
            emitter.emit("shippingAddressCompleted");
        })
        .catch((error) => {
            if (
                typeof error.response === "undefined" ||
                !Object.prototype.hasOwnProperty.call(error.response, "data") ||
                !Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                return;
            }

            for (const field in error.response.data.errors) {
                errors[field] = error.response.data.errors[field][0];
            }
        })
        .finally(() => (loading.value = false));
}
</script>
